import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { signOut as firebaseSignOut } from 'firebase/auth';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/theme-context';
import { Toaster } from 'react-hot-toast';
import { firebaseAuth } from './lib/firebase';
import { PostHogTracker } from './components/posthog-tracker';

// Layouts
import RootLayout from './layouts/RootLayout';
import DashboardLayout from './layouts/DashboardLayout';
// Embed bridge (must init before ProtectedRoute)
import { useOptiSignsBridge } from './embed/useOptiSignsBridge';

// Auth Pages
import Login from './routes/auth/Login';
import Signup from './routes/auth/Signup';
import AuthCallback from './routes/auth/callback';
import ContentAuthStart from './routes/auth/ContentAuthStart';
import ContentPass from './routes/auth/ContentPass';

// Dashboard Pages
import Dashboard from './routes/dashboard/Dashboard';
import EditorList from './routes/dashboard/editor/EditorList';
import NewProject from './routes/dashboard/editor/NewProject';
import EditorDetail from './routes/dashboard/editor/EditorDetail';
import PowerEditor from './routes/dashboard/editor/PowerEditor';
import PresenterRoute from './routes/dashboard/editor/PresenterRoute';
import Templates from './routes/dashboard/Templates';
import Apps from './routes/dashboard/apps';
import PromptLibrary from './routes/dashboard/prompt-library/index';
import PromptDetail from './routes/dashboard/prompt-library/[promptId]';
import SkillsMarketplace from './routes/dashboard/skills-marketplace/index';
import SkillDetail from './routes/dashboard/skills-marketplace/skill-detail';
import CustomSkillEditor from './routes/dashboard/skills-marketplace/custom-skill-editor';
import CustomSkillDetail from './routes/dashboard/skills-marketplace/custom-skill-detail';
import Settings from './routes/dashboard/Settings';
import DevContainers from './routes/dashboard/settings/DevContainers';
import OptiDevCloud from './routes/dashboard/optidev-cloud/OptiDevCloud';
import CloneFromEnv from './routes/dashboard/CloneFromEnv';
import Connectors from './routes/dashboard/Connectors';
import Secrets from './routes/dashboard/Secrets';
import InvitePage from './routes/invite';
import ConnectionOAuthCallback from './routes/connections/oauth/callback';
import CheckoutSuccess from './routes/billing/checkout-success';

// Protected Route Wrapper
import ProtectedRoute from './components/ProtectedRoute';
import { FeatureGate } from './components/FeatureGate';

// Slides Landing Page (lazy-loaded for slides.app.optidev.ai subdomain)
const SlidesFromUrlPage = lazy(() => import('./routes/slides/slides-from-url'));

// Kiosk Landing Page (lazy-loaded for kiosk.app.optidev.ai subdomain)
const KioskFromUrlPage = lazy(() => import('./routes/kiosk/kiosk-from-url'));

// Admin Pages (lazy-loaded)
const AdminCampaigns = lazy(() => import('./routes/dashboard/admin/campaigns/index'));
const AdminCampaignDetail = lazy(() => import('./routes/dashboard/admin/campaigns/[campaignId]'));
const AdminCampaignCreate = lazy(() => import('./routes/dashboard/admin/campaigns/create'));
const AdminAppConfig = lazy(() => import('./routes/dashboard/admin/app-config'));

// Logout Handler Component
function LogoutHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const logoutParam = searchParams.get('logout');

    if (logoutParam === 'true') {
      const performLogout = async () => {
        try {
          // Sign out locally to clear Firebase session
          await firebaseSignOut(firebaseAuth);

          // Remove logout parameter from URL
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('logout');
          const newUrl = newParams.toString() ? `/?${newParams.toString()}` : '/';
          navigate(newUrl, { replace: true });
        } catch (error) {
          console.error('Logout failed:', error);
          // Even on error, remove the parameter
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('logout');
          const newUrl = newParams.toString() ? `/?${newParams.toString()}` : '/';
          navigate(newUrl, { replace: true });
        }
      };

      performLogout();
    }
  }, [searchParams, navigate]);

  return null;
}

// Detect slides subdomain (production only — hostname-based)
const isSlidesDomain = window.location.hostname.startsWith('slides.');

// Detect kiosk subdomain (production only — hostname-based)
const isKioskDomain = window.location.hostname.startsWith('kiosk.');

function RedirectToMainDomain() {
  useEffect(() => {
    const mainHost = window.location.hostname.replace(/^(slides|kiosk)\./, '');
    const port = window.location.port ? `:${window.location.port}` : '';
    window.location.href = `${window.location.protocol}//${mainHost}${port}${window.location.pathname}${window.location.search}`;
  }, []);
  return null;
}

function AppRoutes() {
  // Initialize embed bridge BEFORE ProtectedRoute — bridge must send ON_INIT
  // to parent to get auth token, which sets user and unblocks ProtectedRoute
  useOptiSignsBridge();

  // Slides subdomain (production): dedicated slides app with catch-all route
  if (isSlidesDomain) {
    return (
      <Routes>
        <Route path="auth">
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="callback" element={<AuthCallback />} />
        </Route>
        {/* Redirect dashboard to main domain — slides subdomain doesn't serve dashboard */}
        <Route path="dashboard/*" element={<RedirectToMainDomain />} />
        <Route path="/*" element={<Suspense fallback={null}><SlidesFromUrlPage /></Suspense>} />
      </Routes>
    );
  }

  // Kiosk subdomain (production): dedicated kiosk app with catch-all route
  if (isKioskDomain) {
    return (
      <Routes>
        <Route path="auth">
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="callback" element={<AuthCallback />} />
        </Route>
        {/* Redirect dashboard to main domain — kiosk subdomain doesn't serve dashboard */}
        <Route path="dashboard/*" element={<RedirectToMainDomain />} />
        <Route path="/*" element={<Suspense fallback={null}><KioskFromUrlPage /></Suspense>} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<RootLayout />}>
        <Route index element={<Navigate to="/dashboard" replace />} />

        {/* Signing-in route - redirects to home if user refreshes during OAuth flow */}
        <Route path="signing-in" element={<Navigate to="/" replace />} />

        <Route path="auth">
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="callback" element={<AuthCallback />} />
          <Route path="content-start" element={<ContentAuthStart />} />
          <Route path="content-pass" element={<ContentPass />} />
        </Route>

        {/* Workspace invitation landing page */}
        <Route path="invite/:token" element={<InvitePage />} />

        {/* Connection OAuth callback */}
        <Route path="connections/oauth/callback" element={<ConnectionOAuthCallback />} />

        {/* Slides from URL — local dev support for path-based URLs like /https://example.com */}
        <Route path="https:/*" element={<Suspense fallback={null}><SlidesFromUrlPage /></Suspense>} />
        <Route path="http:/*" element={<Suspense fallback={null}><SlidesFromUrlPage /></Suspense>} />

        {/* Stripe Checkout success (embed opens in new tab — auto-closes. Standalone falls back to dashboard.) */}
        <Route path="billing/checkout-success" element={<CheckoutSuccess />} />

        {/* Presenter view — top-level so the new popup window doesn't render the
            DashboardLayout sidebar/nav. Only the PresenterDashboard fills the
            window. ProtectedRoute still enforces auth. */}
        <Route
          path="presenter/:projectId"
          element={
            <ProtectedRoute>
              <PresenterRoute />
            </ProtectedRoute>
          }
        />

        <Route
          path="dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
              <Route index element={<Dashboard />} />
              <Route path="editor" element={<EditorList />} />
              <Route path="editor/new" element={<NewProject />} />
              <Route path="editor/:projectId" element={<EditorDetail />} />
              <Route path="editor/:projectId/power" element={<PowerEditor />} />
              <Route path="templates" element={<FeatureGate featureKey="templates"><Templates /></FeatureGate>} />
              <Route path="apps" element={<Apps />} />
              <Route path="prompt-library" element={<PromptLibrary />} />
              <Route path="skills" element={<SkillsMarketplace />} />
              <Route path="skills/custom/new" element={<CustomSkillEditor />} />
              <Route path="skills/custom/:id" element={<CustomSkillDetail />} />
              <Route path="skills/custom/:id/edit" element={<CustomSkillEditor />} />
              <Route path="skills/:skillId" element={<SkillDetail />} />
              <Route path="prompt-library/:promptId" element={<PromptDetail />} />
              <Route path="connectors" element={<Connectors />} />
              <Route path="secrets" element={<Secrets />} />
              <Route path="settings">
                <Route index element={<Navigate to="/dashboard/settings/billing" replace />} />
                <Route path=":tab" element={<Settings />} />
                <Route path="workspaces" element={<DevContainers />} />
              </Route>
              <Route path="optidev-cloud/:projectId" element={<OptiDevCloud />} />
              {/* Cross-env platform-admin clone landing page (target = sandbox1) */}
              <Route path="clone" element={<CloneFromEnv />} />
              {/* Admin routes (lazy-loaded, platform admin only) */}
              <Route path="admin/campaigns" element={<Suspense fallback={null}><AdminCampaigns /></Suspense>} />
              <Route path="admin/campaigns/new" element={<Suspense fallback={null}><AdminCampaignCreate /></Suspense>} />
              <Route path="admin/campaigns/:campaignId" element={<Suspense fallback={null}><AdminCampaignDetail /></Suspense>} />
              <Route path="admin/app-config" element={<Suspense fallback={null}><AdminAppConfig /></Suspense>} />
          </Route>
        </Route>
      </Routes>
  );
}

function App() {
  return (
    <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <ThemeProvider>
        <AuthProvider>
          <LogoutHandler />
          <PostHogTracker />
          <AppRoutes />
        <Toaster
          position="bottom-right"
          containerStyle={{
            zIndex: 99999,
          }}
          toastOptions={{
            duration: 4000,
            style: {
              background: 'hsl(var(--card))',
              color: 'hsl(var(--foreground))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '6px',
              padding: '10px 14px',
              fontSize: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              cursor: 'pointer',
            },
            success: {
              iconTheme: {
                primary: '#22c55e',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;